(ns app.front.view.inspections.list
  (:require [app.domain.booking.model :as booking-model]
            [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.entity-edit :as ee]
            [app.front.process :as process]
            [app.front.ui.dnd :as dnd]
            [app.front.ui.error-list :as ui-error-list]
            [app.front.ui.placeholder-button :as ui-placeholder-button]
            [app.front.ui.section :as section]
            [app.front.util :refer [for-frag]]
            [app.front.view.inspections.add :as inspection-add]
            [app.front.view.inspections.deficiencies-v2 :as deficiencies-v2]
            [app.front.view.inspections.edit :as inspection-edit]
            [app.front.view.inspections.event-list :as inspection-event-list]
            [app.front.view.inspections.events :as inspection-events]
            [app.front.view.workspace.get :as ws-get]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(rf/reg-sub ::editing?
  (fn [db [_ inspection-id]]
    (-> db ::editing-id (= inspection-id))))

(rf/reg-event-db ::start-edit
  (fn [db [_ inspection-id]]
    (assoc db ::editing-id inspection-id)))

(rf/reg-event-db ::end-edit
  (fn [db _]
    (dissoc db ::editing-id)))

(def extract-inspection-id (partial entities/id-of-some-in-apply-changes
                                    :entity.type/inspection))

(rf/reg-event-db ::added
  (fn [db [_ result]]
    (assoc db ::editing-id (extract-inspection-id result))))

(defn transform-value [attr value]
  (if (= attr :inspection/inspector-qualifications)
    (du/entity-id-set value)
    value))

(defn send-update-inspection [execution insp-id attr value]
  (>evt [::process/signal
         execution
         [:process.signal/update-inspection
          {:inspection-id insp-id
           :attr          attr
           :value         (transform-value attr value)}]]))


(defn save-value-locally [insp-id attr value]
  (>evt [::ee/set-value insp-id attr (transform-value attr value)]))


(def save-changes (ee/save-changes-fn :process.signal/update-inspection))

(defn button-with-spinner [{:keys [signal disabled text on-click]}]
  (let [processing? (<sub [::process/signal-processing? signal])]
    [:<>
     (cc/button
       {:kind       :primary
        :class-name "ml-2"
        :on-click   on-click
        :icon       i/save
        :disabled   (boolean (or disabled processing?))}
       text)
     (when processing?
       [:div.p-4
        (cc/loading-spinner {:small true :with-overlay false})])]))

(defn inspection-editor [{:keys [tr target-entity] :as props}
                         {state         :domain/state
                          inspection-id :db/id
                          :as           inspection}]
  (let [is-draft?    (= :inspection.state/draft state)
        has-changes? (<sub [::ee/has-changes? inspection-id])]
    [inspection-edit/editor
     (merge props {:on-update (if is-draft?
                                (partial send-update-inspection target-entity)
                                save-value-locally)
                   :on-remove #(>evt [::end-edit])})
     inspection
     (when (not is-draft?)
       [button-with-spinner {:signal   :process.signal/update-inspection
                             :disabled (not has-changes?)
                             :text     (tr [:save])
                             :on-click #(save-changes target-entity inspection-id {:id-keyword :inspection-id})}])
     (let [activate-signal [:process.signal/activate-inspection {:inspection-id inspection-id}]
           can-activate?   (<sub [::process/signal-permitted? target-entity activate-signal])]
       (when can-activate?
         [button-with-spinner {:signal   activate-signal
                               :text     (tr [:save])
                               :on-click #(>evt [::process/signal target-entity activate-signal])}]))]))

(defn edit-toggle [{:keys [tr target-entity]} inspection-id]
  (let [update-signal [:process.signal/update-inspection {:inspection-id inspection-id}]
        editing?      (<sub [::editing? inspection-id])]
    (when (<sub [::process/signal-permitted? target-entity update-signal])
      (cc/button
        {:size             :small
         :kind             :ghost
         :on-click         (if editing?
                             #(>evt [::end-edit])
                             #(>evt [::start-edit inspection-id]))
         :icon             (if editing? i/remove i/pen)
         :icon-description ""}
        (tr [(if editing? :close :edit)])))))

(defn reorder-handle [{:keys [tr augment-draggable-props target-entity]} inspection]
  (when (and (<sub [::process/signal-permitted?
                    target-entity
                    [:process.signal/reorder-inspections]])
             augment-draggable-props)
    [:div.inline-block (augment-draggable-props {} inspection)
     (cc/icon {:icon        i/draggable
               :description (tr [:reorder])})]))


(defn authority-actions [{:keys [*auth-actions-visible?] :as props}
                         {inspection-id :db/id :as inspection}]
  [:div {:class ["flex flex-row items-center"
                 (when-not @*auth-actions-visible? "hidden-if-hover-device")]}
   [edit-toggle props inspection-id]
   [reorder-handle props inspection]])

(defn open-inspection-button [{:keys [tr inspection-id workspace-id]}]
  (cc/button
    {:href             (href/href :view/inspection-visit
                                  {:inspection-id inspection-id
                                   :workspace-id  workspace-id})
     :icon             i/arrow-right
     :icon-description ""
     :size             :small
     :kind             :ghost
     :data-cy          "open-inspection"
     :class-name       "inline-flex align-top"}
    (tr [:open])))

(defn inspection-summary [{{workspace-id    :workspace/id
                            permit-type     :workspace/permit-type
                            authority-group :domain/authority-group} :workspace
                           :keys                                     [tr]
                           :as                                       props}
                          {state         :domain/state
                           inspection-id :db/id
                           :as           inspection}]
  (let [cancelled?                   (= :inspection.state/cancelled state)
        lang                         (<sub [:language])
        inspection-priority-enabled? (and (<sub [::ff/enabled? :feature-flag.id/inspection-priority])
                                          (group-model/has-feature? authority-group :group.feature/inspection-priority))
        not-visible-to-applicant?    (and (<sub [::ff/enabled? :feature-flag.id/pre-issuance-inspections])
                                          (<sub [:session/authority-user?])
                                          (not (inspection-model/inspection-visible-to-applicant? permit-type inspection)))]
    [:header.mb-4
     [:div.flex.flex-row.items-center
      (if (and inspection-priority-enabled?
               (:inspection/priority inspection))
        [:div.flex.flex-row.gap-2.items-center.flex-auto
         [:h3.mb-2.font-bold.text-base.mr-2
          {:class (when cancelled? "line-through")}
          (inspection-model/inspection-name inspection lang)]
         [:div.rounded-full.border-1.py-3.px-5.border-solid.font-bold.bg-ui-violet.text-white.border-ui-violet
          (tr [(:inspection/priority inspection)])]]
        [:h3.mb-2.font-bold.text-base.flex-auto
         {:class (when cancelled? "line-through")}
         (inspection-model/inspection-name inspection lang)])
      [:div.flex.flex-row.items-center
       [authority-actions props inspection]
       [open-inspection-button (util/mmap tr inspection-id workspace-id)]]]
     (if (<sub [::editing? inspection-id])
       [inspection-editor props inspection]
       [:p.mb-2.pb-0.text-sm.leading-normal
        {:class (when cancelled? "line-through")}
        (inspection-model/inspection-description inspection lang)])
     (when not-visible-to-applicant?
       [:p.mb-2.pb-0.text-sm.leading-normal.text-status-attention
        (tr [:inspection/not-visible-to-applicant])])]))

(rf/reg-sub ::applicable-errors
  (fn [[_ application-id signal]]
    (rf/subscribe [::process/signal-errors-for-error-list application-id signal]))
  (fn [errors _]
    (->> errors
         ;; some guards are not interesting in the error list, or are too old
         (remove (comp #{:process.guard/workspace-child-in-state
                         :process.guard/no-valid-bookings} first)))))

(rf/reg-sub ::phase-inspections
  (fn [_ [_ target-entity]]
    (inspection-model/phase-inspections target-entity)))

(defn render-inspection-dependency-item
  "Render a single dependency item."
  [dependency inspections lang tr]
  (let [req-kind      (:inspection-dependency/required-template-kind dependency)
        req-kind-name (inspection-model/inspection-template-kind-name req-kind lang)
        req-results   (:inspection-dependency/required-visit-results dependency)
        result-names  (map #(inspection-model/visit-result-name % lang) req-results)
        is-satisfied? (inspection-model/dependency-satisfied? dependency inspections)]
    ^{:key (:db/id dependency)}
    [:li.flex.items-start.mb-2
     [:div.mr-2.mt-0.5
      (if is-satisfied?
        [cc/icon {:icon i/check :description "" :width "16" :height "16"}]
        [cc/icon {:icon i/circle-dash :description "" :width "16" :height "16"}])]
     [:div
      [:div.font-medium req-kind-name]
      (when (seq result-names)
        [:div.text-gray-600
         (tr [:inspection.dependencies/acceptable-results-item]) (str/join ", " result-names)])]]))

(defn render-inspection-dependencies
  "Render the dependencies accordion with all dependency items."
  [dependencies target-entity lang tr]
  (when (seq dependencies)
    (let [inspections            (<sub [::phase-inspections target-entity])
          workspace-dependencies (inspection-model/workspace-dependencies inspections dependencies)]
      (when (seq workspace-dependencies)
        [cc/accordion
         {:class-name "mb-2"}
         [cc/accordion-item
          {:title (tr [:inspection.dependencies/inspection-has-dependencies])}
          [:div
           [:p.mb-2.text-sm.text-gray-700
            (tr [:inspection.dependencies/acceptable-result-label])]
           [:ul.list-none.ml-0.mt-2.text-sm.text-gray-700
            (for [dep workspace-dependencies]
              (render-inspection-dependency-item dep inspections lang tr))]]]]))))

(defn inspection-quick-pick-deficiencies-list [inspection tr]
  (let [workspace               (inspection-model/workspace inspection)
        template-kind           (inspection-model/inspection-template-kind inspection)
        ws-leaf-config-id       (-> workspace workspace-model/leaf-config :db/id)
        quick-pick-deficiencies (inspection-model/active-quick-pick-deficiencies template-kind ws-leaf-config-id)]
    [section/subsection-2 (util/mmap {:title                (tr [:deficiencies/typical])
                                      :title-class          "text-sm"
                                      :class                "bg-white my-4 px-4 py-1 rounded"
                                      :initially-collapsed? true}
                                     tr)
     (if (empty? quick-pick-deficiencies)
       [:p.pb-3.text-sm.text-gray-700
        (tr [:deficiencies/quick-pick-list-empty])]
       [:div.pb-4
        [deficiencies-v2/quick-pick-deficiencies-table (take 10 quick-pick-deficiencies) tr]])]))

(defn render-inspection
  "Render a single inspection, including its bookings and visits"
  [{:keys [augment-target-props tr target-entity]
    :as   props
    :or   {augment-target-props (fn [v & _] v)}}
   {inspection-id :db/id
    :as           inspection}
   on-create-booking]
  (r/with-let [*auth-actions-visible? (r/atom false)]
    (let [lang                  (<sub [:language])
          inspections           (<sub [::phase-inspections target-entity])
          dependencies          (inspection-model/workspace-inspection-dependencies inspection inspections)
          authority-user?       (<sub [:session/authority-user?])
          can-request?          (inspection-model/can-request-inspection? inspection inspections authority-user?)
          has-documented-visit? (some? (inspection-model/get-latest-visit-result inspection))
          create-booking-signal [:process.signal/create-booking {:inspection-id inspection-id}]
          auth-group            (<sub [::ws-subs/authority-group])
          workdays-group        (if (group-model/superdomain-calendar? auth-group)
                                  (group-model/group-superdomain auth-group)
                                  auth-group)
          draw-request-button?  (and (-> workdays-group
                                         booking-model/workdays-for-group
                                         seq)
                                     can-request?)
          is-draft?             (= :inspection.state/draft (:domain/state inspection))]
      [:div.px-4.pt-4.pb-2.mb-4
       (-> {:data-cy        "single-inspection"
            :class          (if is-draft?
                              ui-placeholder-button/dashed-classes-gray
                              "bg-gray-200")
            :on-mouse-enter #(reset! *auth-actions-visible? true)
            :on-mouse-leave #(reset! *auth-actions-visible? false)}
           (augment-target-props inspection))
       [inspection-summary
        (assoc props :*auth-actions-visible? *auth-actions-visible?)
        inspection]
       [inspection-event-list/inspection-items
        {:tr tr}
        inspection]
       (when (and (not has-documented-visit?)
                  (<sub [::ff/enabled? :feature-flag.id/inspection-dependencies]))
         (render-inspection-dependencies dependencies target-entity lang tr))
       (when (group-model/has-feature? (-> target-entity :entity/parent :domain/authority-group)
                                       :group.feature/deficiency-quick-pick-list-public-display)
         [inspection-quick-pick-deficiencies-list inspection tr])
       (if (<sub [::process/signal-permitted? target-entity create-booking-signal])
         (cond
           draw-request-button?
           [ui-placeholder-button/placeholder-button
            {:title    (if (and (<sub [::ff/enabled? :feature-flag.id/pre-issuance-inspections])
                                authority-user?)
                         (tr [:booking/book-inspection])
                         (tr [:booking/request-inspection]))
             :on-click (fn [] (on-create-booking inspection))
             :class    "my-2"}]
           (<sub [::ws-get/fetching-config? (-> target-entity :entity/parent :domain/authority-group :group/id)])
           [:div.w-full.flex.justify-around (cc/button-skeleton)]
           :else nil)
         (when-not (<sub [::process/signal-forbidden? target-entity create-booking-signal])
           (when-let [errors (<sub [::applicable-errors target-entity create-booking-signal])]
             (when (and draw-request-button? (seq errors))
               [:div.flex.flex-col.lg:flex-row.justify-between.mt-4
                [:p (tr [:inspection/request-prevented-by])]
                [:div#error-list.flex.flex-col
                 [ui-error-list/signal-error-list tr errors]]]))))])))


(defn render-inspection-list
  "Render all inspections, with 'past inspections' (inspections that have a booking) and the next upcoming one
  visible and the reset of the future inspections hidden by default"
  [{:keys [target-entity] :as props}
   past-inspections
   future-inspections
   on-create-booking]
  (let [signal         [:process.signal/add-inspection]
        add-permitted? (<sub [::process/signal-permitted? target-entity signal])]
    [:<>
     (for-frag [insp past-inspections]
       ^{:key (:db/id insp)} [render-inspection props insp on-create-booking])
     (when (seq future-inspections)
       [dnd/wrap-dnd
        {:id               (str "inspection-events-dnd-" (:db/id target-entity))
         :items            future-inspections
         :props-for-source {:class "dnd-hilight"}
         :on-reorder       (partial inspection-events/on-reorder target-entity)}
        (fn [{:keys [sorted] :as draggable-props}]
          (for-frag [insp sorted]
            ^{:key (:db/id insp)}
            [render-inspection
             (merge props
                    draggable-props)
             insp
             on-create-booking
             draggable-props]))])
     (when add-permitted?
       [:div.mb-4
        [inspection-add/add-component (assoc props
                                             :on-add-dispatch [::added])]])]))
